import "./TagList.scss";

import { useSelector } from "react-redux";
import { ModalXIcon } from "../../../assets/icons";
import { useEffect, useRef, useState } from "react";
import {
  draggable,
  monitorForElements,
  dropTargetForElements,
} from "@atlaskit/pragmatic-drag-and-drop/element/adapter";

const TagList = ({
  items = [],
  labelKey = "label",
  onRemove = () => {},
  onClick,
  style = {},
  onReorder = () => {},
  dndDisabled = true,
  itemColor = "grey", // "grey" | "white"
}) => {
  const containerRef = useRef(null);
  const [globalDragState, setGlobalDragState] = useState(false);
  const { isDarkMode } = useSelector((state) => state.usersList);

  useEffect(() => {
    if (dndDisabled) return;

    const element = containerRef.current;
    if (!element) return;

    const cleanupMonitor = monitorForElements({
      onDragStart() {
        setGlobalDragState(true);
      },
      onDrop({ source, location }) {
        setGlobalDragState(false);

        const destination = location.current.dropTargets[0];
        if (!destination) return;

        const sourceIndex = source.data.index;
        const destinationIndex = destination.data.index;
        const edge = destination.data.edge;

        let finalIndex = destinationIndex;
        if (edge === "right") {
          finalIndex = destinationIndex + 1;
        }

        if (sourceIndex < finalIndex) {
          finalIndex--;
        }

        if (finalIndex === undefined || sourceIndex === finalIndex) {
          return;
        }

        const newItems = [...items];
        const [movedItem] = newItems.splice(sourceIndex, 1);
        newItems.splice(finalIndex, 0, movedItem);

        onReorder(newItems);
      },
    });

    const cleanupDropTarget = dropTargetForElements({
      element,
    });

    return () => {
      cleanupMonitor();
      cleanupDropTarget();
    };
  }, [onReorder, dndDisabled, items]);

  return (
    <div
      ref={containerRef}
      className={`tagListContainer ${globalDragState ? "drag-active" : ""} ${
        isDarkMode ? "dark" : ""
      }`}
      style={style}
    >
      {items.map((item = {}, index) => (
        <DraggableTagItem
          key={item.id || index}
          item={item}
          index={index}
          onClick={onClick}
          labelKey={labelKey}
          onRemove={onRemove}
          itemColor={itemColor}
          dndDisabled={dndDisabled}
          globalDragState={globalDragState}
        />
      ))}
    </div>
  );
};

const DraggableTagItem = ({
  item,
  index,
  onClick,
  labelKey,
  onRemove,
  itemColor,
  dndDisabled,
  globalDragState,
}) => {
  const ref = useRef(null);
  const [isDragging, setIsDragging] = useState(false);
  const [isDraggedOver, setIsDraggedOver] = useState(false);
  const [dropIndicator, setDropIndicator] = useState(null);

  useEffect(() => {
    if (dndDisabled) return;

    const element = ref.current;
    if (!element) return;

    const cleanup = [];

    cleanup.push(
      draggable({
        element,
        getInitialData: () => ({ item, index }),
        onDragStart: () => setIsDragging(true),
        onDrop: () => setIsDragging(false),
      })
    );

    cleanup.push(
      dropTargetForElements({
        element,
        getData: ({ input }) => {
          const rect = element.getBoundingClientRect();
          const midpoint = rect.left + rect.width / 2;
          const edge = input.clientX < midpoint ? "left" : "right";
          return { index, edge };
        },
        canDrop: ({ source }) => source.data.index !== index,
        onDragEnter: ({ self }) => {
          setDropIndicator(self.data.edge);
        },
        onDragLeave: () => setDropIndicator(null),
        onDrop: () => setDropIndicator(null),
      })
    );

    return () => cleanup.forEach((fn) => fn());
  }, [item, index, dndDisabled]);

  return (
    <div
      ref={ref}
      className={`tagListItem ${itemColor}`}
      // ${isDragging ? "dragging" : ""}
      onClick={(e) => {
        typeof onClick === "function" && onClick(item);
      }}
      style={{
        cursor: !!onClick
          ? "pointer"
          : dndDisabled
          ? "default"
          : isDragging
          ? "grabbing"
          : "pointer",
        position: "relative",
      }}
    >
      {item[labelKey] || "No Label"}
      <span
        className="removeTagIcon"
        onClick={(e) => {
          e.stopPropagation();
          onRemove(item);
        }}
      >
        <ModalXIcon width={10} height={10} />
      </span>

      {/* Drop indicators */}
      {dropIndicator === "left" && (
        <div className="drop-indicator drop-indicator--left" />
      )}
      {dropIndicator === "right" && (
        <div className="drop-indicator drop-indicator--right" />
      )}
    </div>
  );
};

export default TagList;
